import { Loader2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { FormDatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { FormRadioGroup } from '@/components/ui/radio-group';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useIndeedEndDateTypeOptions } from '@/hooks/use-indeed-end-date-type-options';
import { INDEED, JOVEO, ZIPRECRUITER } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';
import { addDays, durationInDays } from '@/utils/date-util';

import { getChannelDefaults } from '../channel-defaults';
import { BudgetType } from '../types';
import { BudgetAllocation } from './budget-allocation';
import { BudgetCalculationSection } from './budget-calculation-section';
import {
  ChannelFormProvider,
  useChannelFormContext,
} from './channel-form-context';
import { Conflicts } from './conflicts';
import { ZipRecruiterBudget } from './zip-recruiter-budget';
interface ChannelFormProps {
  channelSlug: string;
}

export function ChannelForm({ channelSlug }: ChannelFormProps) {
  return (
    <ChannelFormProvider channelSlug={channelSlug}>
      <ChannelFormContent channelSlug={channelSlug} />
    </ChannelFormProvider>
  );
}

function ChannelFormContent({ channelSlug }: ChannelFormProps) {
  const intl = useIntl();
  const { budgetTypeOptions, getJobPostingBudgetLabel } = useBudgetType();
  const indeedEndDateTypeOptions = useIndeedEndDateTypeOptions();
  const {
    isPending,
    validateChannelBudget,
    removeChannelFromPosting,
    cancelChannelChanges,
    validateChannelChanges,
    openingsWithConflicts,
    handleBudgetTypeChange,
    clearConflicts,
    removeConflictingOpening,
    conflicts,
  } = useChannelFormContext();
  const { hookForm, selectedChannelSlugs } = useJobPostingContext();
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, dirtyFields },
    clearErrors,
  } = hookForm;

  const channelBudget = watch(`channels.${channelSlug}.channelBudget`);
  const totalChannelBudget = watch(
    `channels.${channelSlug}.totalChannelBudget`,
  );
  const openingBudgets = watch(`channels.${channelSlug}.openingBudgets`);
  const budgetType = watch(`channels.${channelSlug}.budgetType`);
  const startsAt = watch(`channels.${channelSlug}.startsAt`);
  const endAt = watch(`channels.${channelSlug}.endAt`);
  const duration = endAt && startsAt ? durationInDays(startsAt, endAt) : null;

  const channelDefaults = getChannelDefaults(channelSlug);
  const isBudgetReadOnly = !!channelDefaults.budget;
  const isEndDateReadOnly = !!channelDefaults.duration;
  const minDuration =
    channelDefaults.minDuration ?? channelDefaults.duration ?? 1;

  const isChannelDirty = dirtyFields?.channels?.[channelSlug];
  // Reset the validated status when the channel is dirty so that the channel can be validated again
  useEffect(() => {
    if (isChannelDirty) setValue(`channels.${channelSlug}.isValidated`, false);
  }, [channelSlug, isChannelDirty, setValue]);

  // Calculate minimum end date based on start date and channel constraints
  const minEndDate = useMemo(() => {
    if (!startsAt) return undefined;

    const startDate = new Date(startsAt);
    return addDays(startDate, minDuration);
  }, [startsAt, minDuration]);

  const handleDateChange = () => {
    validateChannelBudget();
    clearConflicts(); // Clear conflicts when date changes
    clearErrors(`channels.${channelSlug}.endAt`);
  };

  // Handle start date change and auto-update end date based on constraints
  const handleStartDateChange = (newStartDate: string) => {
    const startDate = new Date(newStartDate);
    const calculatedEndDate = addDays(startDate, minDuration).toISOString();

    // Case 1: Fixed duration - always set the end date
    if (channelDefaults.duration) {
      setValue(`channels.${channelSlug}.endAt`, calculatedEndDate);
      return;
    }

    // Case 2: Minimum duration - only set if not set or if it violates minimum
    if (channelDefaults.minDuration || minDuration) {
      const currentEndDate = endAt ? new Date(endAt) : null;
      if (!currentEndDate || currentEndDate < new Date(calculatedEndDate)) {
        setValue(`channels.${channelSlug}.endAt`, calculatedEndDate);
      }
    }
    handleDateChange();
  };

  // Check if all openings selected in a channel have conflicts
  const allOpeningsHaveConflicts = useMemo(
    () =>
      Object.keys(openingBudgets).every(
        id => openingsWithConflicts && openingsWithConflicts.includes(id),
      ),
    [openingBudgets, openingsWithConflicts],
  );

  return (
    <div className="border-fx-neutral-200 mb-2 w-[640px] rounded-lg border bg-white py-6 px-12 shadow-sm">
      <div className="flex flex-1 flex-col gap-6 ">
        <ChannelIcon
          channelSlug={channelSlug}
          variant="large"
          className="mx-auto"
        />

        {/* Budget type for Joveo */}
        {channelSlug === JOVEO && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelSlug}.budgetType`}
            options={budgetTypeOptions}
            radioGroupClassName="justify-between"
            onChange={value => handleBudgetTypeChange(value as BudgetType)}
          />
        )}

        {/* End date type for Indeed */}
        {channelSlug === INDEED && (
          <FormRadioGroup
            control={control}
            name={`channels.${channelSlug}.endAtType`}
            options={indeedEndDateTypeOptions}
          />
        )}

        {channelSlug === ZIPRECRUITER ? (
          <ZipRecruiterBudget channelSlug={channelSlug} />
        ) : (
          <Input
            isNumber
            {...register(`channels.${channelSlug}.channelBudget`, {
              setValueAs: value =>
                value === '' || value === null ? null : Number(value),
              onChange: validateChannelBudget,
            })}
            label={getJobPostingBudgetLabel(budgetType)}
            placeholderLabel="$"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter total spend for the channel',
              description:
                'Placeholder for the total spend input for the channel',
            })}
            readOnly={isBudgetReadOnly}
            error={errors?.channels?.[channelSlug]?.channelBudget?.message}
            required
          />
        )}

        <BudgetAllocation channelSlug={channelSlug} />

        <div className="flex gap-6">
          {/* Campaign start date */}
          <div className="flex-1">
            <FormDatePicker
              isStartOfDay
              control={control}
              name={`channels.${channelSlug}.startsAt`}
              label={intl.formatMessage({
                defaultMessage: 'Start date',
                description:
                  'Form label for when the job board campaign can begin',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter start date',
                description: 'Placeholder for the start date input',
              })}
              onChange={handleStartDateChange}
              required
            />
          </div>

          {/* Campaign end date */}
          <div className="relative flex-1">
            <FormDatePicker
              isEndOfDay
              control={control}
              name={`channels.${channelSlug}.endAt`}
              label={intl.formatMessage({
                defaultMessage: 'End date',
                description:
                  'Form label for when the job board campaign should stop',
              })}
              placeholder={intl.formatMessage({
                defaultMessage: 'Enter end date',
                description: 'Placeholder for the end date input',
              })}
              error={errors?.channels?.[channelSlug]?.endAt?.message}
              readOnly={isEndDateReadOnly}
              minDate={minEndDate}
              onChange={handleDateChange}
              required
            />
            {/* Information icon for ZipRecruiter fixed duration */}
            {(channelDefaults.duration || channelDefaults.minDuration) && (
              <div className="left-18 absolute top-1">
                <Tooltip
                  className="w-72"
                  content={
                    channelDefaults.duration
                      ? intl.formatMessage(
                          {
                            defaultMessage:
                              '{channelName} campaigns have a fixed duration of {duration} days. Your end date is set automatically based on the start date.',
                            description:
                              'Tooltip: fixed campaign length for a job board when duration is set',
                          },
                          {
                            channelName: getChannelName(channelSlug),
                            duration: channelDefaults.duration ?? 0,
                          },
                        )
                      : intl.formatMessage(
                          {
                            defaultMessage:
                              '{channelName} requires a minimum duration of {duration} days. Your end date is set automatically based on the start date.',
                            description:
                              'Tooltip: minimum number of days required for a channel campaign',
                          },
                          {
                            channelName: getChannelName(channelSlug),
                            duration: channelDefaults.minDuration ?? 0,
                          },
                        )
                  }
                >
                  <img src={InfoIcon} alt="Info" />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        {duration && (
          <Typography variant="body1" className="!text-fx-neutral-600 -mt-4">
            <FormattedMessage
              defaultMessage="Duration: {duration} {duration, plural, one {day} other {days}}"
              values={{
                duration: duration ?? '-',
              }}
              description="Summary under dates: {duration} uses ICU plural for day(s)"
            />
          </Typography>
        )}

        <Conflicts
          allOpeningsHaveConflicts={allOpeningsHaveConflicts}
          openingsWithConflicts={openingsWithConflicts}
          removeConflictingOpening={removeConflictingOpening}
          conflicts={conflicts}
        />

        <BudgetCalculationSection
          channelSlug={channelSlug}
          unitBudget={channelBudget}
          totalBudget={totalChannelBudget}
          className="border-fx-neutral-200 bg-fx-neutral-50 rounded-lg border p-4"
        />

        <div className="flex justify-between gap-6">
          {selectedChannelSlugs.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              className="!text-fx-neutral-600"
              onClick={removeChannelFromPosting}
            >
              <FormattedMessage
                defaultMessage="Remove from posting"
                description="Remove the channel from the job posting"
              />
            </Button>
          )}

          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={cancelChannelChanges}
            >
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel the channel changes"
              />
            </Button>
            <Button type="button" onClick={() => void validateChannelChanges()}>
              {isPending && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save the channel changes"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
